import { APIRoutes } from '@/services/api.routes';
import type { 
  VideoID, 
  CourseID, 
  ModuleID, 
  UserVideoProgress, 
} from '@/models';
import type {CourseProgressSummary, ModuleProgressSummary} from "@/modelsAPI";

// Local storage backup for offline functionality
const STORAGE_KEY = 'course_progress_backup';

export interface VideoProgress {
  videoId: string;
  courseId: string;
  moduleId: string;
  watched: boolean;
  watchedAt?: string;
  progress?: number;
}

export interface ProgressData {
  [key: string]: VideoProgress;
}

class CourseProgressService {
  private progressCache = new Map<string, UserVideoProgress>();
  private courseProgressCache = new Map<string, CourseProgressSummary>();
  private moduleProgressCache = new Map<string, ModuleProgressSummary>();

  // Helper methods for local storage backup
  private getBackupData(): ProgressData {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  }

  private saveBackupData(data: ProgressData): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  private getVideoKey(courseId: string, moduleId: string, videoId: string): string {
    return `${courseId}:${moduleId}:${videoId}`;
  }

  // Server-side methods
  async markAsWatched(courseId: string, moduleId: string, videoId: string): Promise<void> {
    try {
      await APIRoutes.updateVideoProgress(videoId as VideoID, {
        progress: 1.0 // Backend expects 0.0-1.0, not 0-100
      });

      // Clear cache to force refresh - clear all progress cache since we need to reload all videos in the module
      this.progressCache.clear();
      this.courseProgressCache.delete(courseId);
      this.moduleProgressCache.delete(moduleId);

      // Update local backup
      const backupData = this.getBackupData();
      const key = this.getVideoKey(courseId, moduleId, videoId);
      backupData[key] = {
        videoId,
        courseId,
        moduleId,
        watched: true,
        watchedAt: new Date().toISOString(),
        progress: 100
      };
      this.saveBackupData(backupData);
    } catch (error) {
      console.error('Failed to mark video as watched on server, updating local backup:', error);
      // Fallback to local storage
      const backupData = this.getBackupData();
      const key = this.getVideoKey(courseId, moduleId, videoId);
      backupData[key] = {
        videoId,
        courseId,
        moduleId,
        watched: true,
        watchedAt: new Date().toISOString(),
        progress: 100
      };
      this.saveBackupData(backupData);
    }
  }

  async markAsUnwatched(courseId: string, moduleId: string, videoId: string): Promise<void> {
    try {
      await APIRoutes.updateVideoProgress(videoId as VideoID, {
        progress: 0.0 // Backend expects 0.0-1.0, not 0-100
      });

      // Clear cache to force refresh - clear all progress cache since we need to reload all videos in the module
      this.progressCache.clear();
      this.courseProgressCache.delete(courseId);
      this.moduleProgressCache.delete(moduleId);

      // Update local backup
      const backupData = this.getBackupData();
      const key = this.getVideoKey(courseId, moduleId, videoId);
      delete backupData[key];
      this.saveBackupData(backupData);
    } catch (error) {
      console.error('Failed to mark video as unwatched on server, updating local backup:', error);
      // Fallback to local storage
      const backupData = this.getBackupData();
      const key = this.getVideoKey(courseId, moduleId, videoId);
      delete backupData[key];
      this.saveBackupData(backupData);
    }
  }

  async isWatched(courseId: string, moduleId: string, videoId: string): Promise<boolean> {
    try {
      // Check cache first
      if (this.progressCache.has(videoId)) {
        const progress = this.progressCache.get(videoId)!;
        console.log(`Video ${videoId} progress from cache:`, progress.progress);
        // Backend always returns progress as 0.0-1.0
        return progress.progress >= 0.95;
      }

      // Fetch module progress from server (since individual video progress GET is no longer available)
      const moduleProgressResponse = await APIRoutes.getModuleProgress(moduleId as ModuleID);
      const moduleProgressList = moduleProgressResponse.videoProgress;
      if (Array.isArray(moduleProgressList)) {
        // Cache all video progress from this module
        moduleProgressList.forEach(progress => {
          this.progressCache.set(progress.videoID, progress);
        });

        // Find progress for this specific video
        const videoProgress = moduleProgressList.find(progress => progress.videoID === videoId);
        if (videoProgress) {
          console.log(`Video ${videoId} progress from server:`, videoProgress.progress);
          // Backend always returns progress as 0.0-1.0
          return videoProgress.progress >= 0.95;
        }
        return false;
      }

      return false;
    } catch (error) {
      console.error('Failed to get module progress from server, checking local backup:', error);
      // Fallback to local storage
      const backupData = this.getBackupData();
      const key = this.getVideoKey(courseId, moduleId, videoId);
      return backupData[key]?.watched === true;
    }
  }

  async updateProgress(courseId: string, moduleId: string, videoId: string, progress: number): Promise<void> {
    try {
      // Convert percentage (0-100) to decimal (0.0-1.0) for backend API
      const progressDecimal = progress / 100;
      
      await APIRoutes.updateVideoProgress(videoId as VideoID, {
        progress: progressDecimal
      });

      // Clear cache to force refresh - clear all progress cache since we need to reload all videos in the module
      this.progressCache.clear();
      this.courseProgressCache.delete(courseId);
      this.moduleProgressCache.delete(moduleId);

      // Update local backup
      const backupData = this.getBackupData();
      const key = this.getVideoKey(courseId, moduleId, videoId);
      if (!backupData[key]) {
        backupData[key] = {
          videoId,
          courseId,
          moduleId,
          watched: false
        };
      }
      backupData[key].progress = progress;
      if (progress >= 95) {
        backupData[key].watched = true;
        backupData[key].watchedAt = new Date().toISOString();
      }
      this.saveBackupData(backupData);
    } catch (error) {
      console.error('Failed to update video progress on server, updating local backup:', error);
      // Fallback to local storage
      const backupData = this.getBackupData();
      const key = this.getVideoKey(courseId, moduleId, videoId);
      if (!backupData[key]) {
        backupData[key] = {
          videoId,
          courseId,
          moduleId,
          watched: false
        };
      }
      backupData[key].progress = progress;
      if (progress >= 95) {
        backupData[key].watched = true;
        backupData[key].watchedAt = new Date().toISOString();
      }
      this.saveBackupData(backupData);
    }
  }

  async getProgress(courseId: string, moduleId: string, videoId: string): Promise<number> {
    try {
      // Check cache first
      if (this.progressCache.has(videoId)) {
        const progress = this.progressCache.get(videoId)!;
        return progress.progress;
      }

      // Fetch from server using module progress
      const moduleProgressResponse = await APIRoutes.getModuleProgress(moduleId as ModuleID);
      const videoProgress = moduleProgressResponse.videoProgress.find(progress => progress.videoID === videoId);

      if (videoProgress) {
        this.progressCache.set(videoId, videoProgress);
        return videoProgress.progress;
      }

      return 0;
    } catch (error) {
      console.error('Failed to get video progress from server, checking local backup:', error);
      // Fallback to local storage
      const backupData = this.getBackupData();
      const key = this.getVideoKey(courseId, moduleId, videoId);
      return backupData[key]?.progress ?? 0;
    }
  }

  async getCourseProgress(courseId: string): Promise<{ watched: number; total: number; percent: number }> {
    try {
      // Check cache first
      if (this.courseProgressCache.has(courseId)) {
        const progress = this.courseProgressCache.get(courseId)!;
        return {
          watched: progress.watchedVideos,
          total: progress.totalVideos,
          percent: progress.progressPercentage
        };
      }

      // Fetch from server - backend returns object with videoProgress array
      const courseProgressResponse = await APIRoutes.getCourseProgress(courseId as CourseID);
      console.log('Course progress API response:', courseProgressResponse);

      const progressList = courseProgressResponse.videoProgress;
      if (!Array.isArray(progressList)) {
        console.error('Invalid progress response format:', courseProgressResponse);
        throw new Error('Invalid API response format: expected videoProgress array');
      }

      // Calculate totals from progress list
      // Backend returns progress as 0.0-1.0
      const watched = progressList.filter(p => p.progress >= 0.95).length;
      const total = progressList.length;
      const percent = total > 0 ? Math.round((watched / total) * 100) : 0;

      // Group progress by modules for caching
      const moduleGroups: { [moduleId: string]: UserVideoProgress[] } = {};
      progressList.forEach(p => {
        if (!moduleGroups[p.moduleID]) {
          moduleGroups[p.moduleID] = [];
        }
        moduleGroups[p.moduleID].push(p);
      });
      
      // Create course progress summary for caching
      const courseProgress = {
        courseID: courseId as CourseID,
        totalVideos: total,
        watchedVideos: watched,
        progressPercentage: percent,
        moduleProgress: Object.keys(moduleGroups).map(moduleId => {
          const moduleProgressList = moduleGroups[moduleId];
          const moduleWatched = moduleProgressList.filter(p => p.progress >= 0.95).length;
          const moduleTotal = moduleProgressList.length;
          const modulePercent = moduleTotal > 0 ? Math.round((moduleWatched / moduleTotal) * 100) : 0;
          
          return {
            moduleID: moduleId as ModuleID,
            totalVideos: moduleTotal,
            watchedVideos: moduleWatched,
            progressPercentage: modulePercent,
            videos: moduleProgressList.map(p => ({
              videoID: p.videoID,
              progress: p.progress,
              watchedAt: p.finishedAt ?? undefined
            }))
          };
        })
      };
      
      this.courseProgressCache.set(courseId, courseProgress);
      
      return {
        watched,
        total,
        percent
      };
    } catch (error) {
      console.error('Failed to get course progress from server, calculating from local backup:', error);
      // Fallback to local storage calculation
      const backupData = this.getBackupData();
      let watched = 0;
      let total = 0;

      Object.values(backupData).forEach(progress => {
        if (progress.courseId === courseId) {
          total++;
          if (progress.watched) {
            watched++;
          }
        }
      });

      return {
        watched,
        total,
        percent: total > 0 ? Math.round((watched / total) * 100) : 0
      };
    }
  }

  async getModuleProgress(courseId: string, moduleId: string): Promise<{ watched: number; total: number; percent: number }> {
    try {
      // Check cache first
      if (this.moduleProgressCache.has(moduleId)) {
        const progress = this.moduleProgressCache.get(moduleId)!;
        return {
          watched: progress.watchedVideos,
          total: progress.totalVideos,
          percent: progress.progressPercentage
        };
      }

      // Fetch from server - backend returns object with videoProgress array
      const moduleProgressResponse = await APIRoutes.getModuleProgress(moduleId as ModuleID);
      console.log('Module progress API response:', moduleProgressResponse);

      const progressList = moduleProgressResponse.videoProgress;
      if (!Array.isArray(progressList)) {
        console.error('Invalid progress response format:', moduleProgressResponse);
        throw new Error('Invalid API response format: expected videoProgress array');
      }

      // Calculate totals from progress list
      // Backend returns progress as 0.0-1.0
      const watched = progressList.filter(p => p.progress >= 0.95).length;
      const total = progressList.length;
      const percent = total > 0 ? Math.round((watched / total) * 100) : 0;

      const moduleProgress = {
        moduleID: moduleId as ModuleID,
        totalVideos: total,
        watchedVideos: watched,
        progressPercentage: percent,
        videos: progressList.map(p => ({
          videoID: p.videoID,
          progress: p.progress,
          watchedAt: p.finishedAt ?? undefined
        }))
      };
      
      this.moduleProgressCache.set(moduleId, moduleProgress);
      
      return {
        watched,
        total,
        percent
      };
    } catch (error) {
      console.error('Failed to get module progress from server, calculating from local backup:', error);
      // Fallback to local storage calculation
      const backupData = this.getBackupData();
      let watched = 0;
      let total = 0;

      Object.values(backupData).forEach(progress => {
        if (progress.courseId === courseId && progress.moduleId === moduleId) {
          total++;
          if (progress.watched) {
            watched++;
          }
        }
      });

      return {
        watched,
        total,
        percent: total > 0 ? Math.round((watched / total) * 100) : 0
      };
    }
  }

  // Sync methods for backward compatibility (these will be async in practice)
  isWatchedSync(courseId: string, moduleId: string, videoId: string): boolean {
    // Check cache first
    if (this.progressCache.has(videoId)) {
      const progress = this.progressCache.get(videoId)!;
      // Backend always returns progress as 0.0-1.0
      return progress.progress >= 0.95;
    }

    // Fallback to local backup for immediate response
    const backupData = this.getBackupData();
    const key = this.getVideoKey(courseId, moduleId, videoId);
    return backupData[key]?.watched === true;
  }

  // Cache management
  clearCache(): void {
    this.progressCache.clear();
    this.courseProgressCache.clear();
    this.moduleProgressCache.clear();
  }

  clearAllProgress(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.clearCache();
  }

  // Legacy methods for backward compatibility
  getTotalProgress(totalVideos: number): { watched: number; total: number; percent: number } {
    const backupData = this.getBackupData();
    const watched = Object.values(backupData).filter(p => p.watched).length;

    return {
      watched,
      total: totalVideos,
      percent: totalVideos > 0 ? Math.round((watched / totalVideos) * 100) : 0
    };
  }

  getWatchedVideos(): VideoProgress[] {
    const backupData = this.getBackupData();
    return Object.values(backupData).filter(p => p.watched);
  }
}

export const courseProgressService = new CourseProgressService();